import axios from 'axios';
import { 
  WorkCenter, 
  Worker, 
  VacationType, 
  VacationRequest, 
  Holiday,
  WorkSchedule,
  WorkerVacationSummary
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Work Centers
export const getWorkCenters = async (): Promise<WorkCenter[]> => {
  const response = await api.get('/work-centers/');
  return response.data;
};

export const createWorkCenter = async (workCenter: Omit<WorkCenter, 'id'>): Promise<WorkCenter> => {
  const response = await api.post('/work-centers/', workCenter);
  return response.data;
};

export const updateWorkCenter = async (id: number, workCenter: Partial<WorkCenter>): Promise<WorkCenter> => {
  const response = await api.put(`/work-centers/${id}`, workCenter);
  return response.data;
};

export const deleteWorkCenter = async (id: number): Promise<void> => {
  await api.delete(`/work-centers/${id}`);
};

// Workers
export const getWorkers = async (): Promise<Worker[]> => {
  const response = await api.get('/workers/');
  return response.data;
};

export const createWorker = async (worker: Omit<Worker, 'id' | 'is_active'>): Promise<Worker> => {
  const response = await api.post('/workers/', worker);
  return response.data;
};

export const updateWorker = async (id: number, worker: Partial<Worker>): Promise<Worker> => {
  const response = await api.put(`/workers/${id}`, worker);
  return response.data;
};

export const deleteWorker = async (id: number): Promise<void> => {
  await api.delete(`/workers/${id}`);
};

// Worker Vacation Summary
export const getWorkersVacationSummary = async (year?: number, bagYear?: number): Promise<WorkerVacationSummary[]> => {
  const params: { year?: number; bag_year?: number } = {};
  if (year) params.year = year;
  if (bagYear) params.bag_year = bagYear;
  
  const response = await api.get('/workers/vacation-summary', { params });
  return response.data;
};

// Get blocked days for a worker (holidays, non-working days, existing requests split by status)
export const getBlockedDays = async (workerId: number, yearFrom: number, yearTo: number): Promise<{
  holidays: string[];
  non_working: string[];
  pending: string[];
  approved: string[];
  rejected: string[];
}> => {
  const response = await api.get('/workers/blocked-days', {
    params: { worker_id: workerId, year_from: yearFrom, year_to: yearTo }
  });
  return response.data;
};

// Count working days for a worker between two dates (excludes weekends and holidays)
export const countWorkingDays = async (workerId: number, startDate: string, endDate: string): Promise<number> => {
  const response = await api.get('/workers/count-working-days', {
    params: { worker_id: workerId, start_date: startDate, end_date: endDate }
  });
  return response.data.working_days;
};

// Get work hours info for each working day in a range (detects mixed schedules)
export const getWorkHoursRange = async (workerId: number, startDate: string, endDate: string): Promise<{
  uniform: boolean;
  work_hours: number;
  working_days: number;
  hours_per_day: { date: string; hours: number }[];
}> => {
  const response = await api.get('/workers/work-hours-range', {
    params: { worker_id: workerId, start_date: startDate, end_date: endDate }
  });
  return response.data;
};

// Vacation Types
export const getVacationTypes = async (): Promise<VacationType[]> => {
  const response = await api.get('/vacation-types/');
  return response.data;
};

// Vacation Requests
export const getVacationRequests = async (workerId?: number): Promise<VacationRequest[]> => {
  const params = workerId ? { worker_id: workerId } : {};
  const response = await api.get('/vacation-requests/', { params });
  return response.data;
};

export const createVacationRequest = async (request: Omit<VacationRequest, 'id' | 'status' | 'approved_by'>): Promise<VacationRequest> => {
  const response = await api.post('/vacation-requests/', request);
  return response.data;
};

export const updateVacationRequest = async (id: number, updates: Partial<VacationRequest>): Promise<VacationRequest> => {
  const response = await api.put(`/vacation-requests/${id}`, updates);
  return response.data;
};

export const deleteVacationRequest = async (id: number): Promise<void> => {
  await api.delete(`/vacation-requests/${id}`);
};

export const getWorkerAvailableHours = async (workerId: number, date: string): Promise<{work_hours: number, available_hours: number, used_hours: number}> => {
  const response = await api.get(`/vacation-requests/work-hours/${workerId}?request_date=${date}`);
  return response.data;
};

// Holidays
export const getHolidays = async (workCenterId?: number, year?: number): Promise<Holiday[]> => {
  const params: any = {};
  if (workCenterId) params.work_center_id = workCenterId;
  if (year) params.year = year;
  
  const response = await api.get('/holidays/', { params });
  return response.data;
};

export const createHoliday = async (holiday: Omit<Holiday, 'id' | 'is_active'>): Promise<Holiday> => {
  const response = await api.post('/holidays/', holiday);
  return response.data;
};

export const updateHoliday = async (id: number, holiday: Partial<Holiday>): Promise<Holiday> => {
  const response = await api.put(`/holidays/${id}`, holiday);
  return response.data;
};

export const deleteHoliday = async (id: number): Promise<void> => {
  await api.delete(`/holidays/${id}`);
};

// Work Schedules  
export const getWorkSchedules = async (workCenterId?: number): Promise<WorkSchedule[]> => {
  const params = workCenterId ? { work_center_id: workCenterId } : {};
  const response = await api.get('/work-schedules/', { params });
  return response.data;
};

export const getWorkCenterSchedule = async (workCenterId: number): Promise<WorkSchedule[]> => {
  const response = await api.get('/work-schedules/', { params: { work_center_id: workCenterId } });
  return response.data;
};

export const updateWorkCenterSchedule = async (
  workCenterId: number, 
  schedules: Array<{ day_of_week: number; work_hours: number; is_working_day: boolean }>
): Promise<WorkSchedule[]> => {
  const response = await api.put(`/work-schedules/work-center/${workCenterId}`, { schedules });
  return response.data;
};

// Reports
export const exportVacationsToExcel = async (year?: number, workerId?: number): Promise<Blob> => {
  const params: any = {};
  if (year) params.year = year;
  if (workerId) params.worker_id = workerId;
  
  const response = await api.get('/reports/export', { 
    params,
    responseType: 'blob'
  });
  
  return response.data;
};

// Auth - Get my vacation summary
export const getMyVacationSummary = async (): Promise<{
  current_year: number;
  current_year_days: {
    total: number;
    used: number;
    remaining: number;
  };
  flexible_hours: {
    total: number;
    used: number;
    remaining: number;
  };
  previous_year?: {
    year: number;
    total: number;
    used: number;
    remaining: number;
  };
}> => {
  const token = localStorage.getItem('token');
  const response = await api.get('/auth/vacation-summary', {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
  return response.data;
};

export default api;
